import {
  createProgressiveRequestHandler,
  createRequestHandler,
} from "../utils/requestHandlers";

import { MediaBaseRouteDelete } from "./media";
import { cfAPI } from "../api";
import { createProgressHandler } from "../utils/createProgressHandler";

// Action Types
const SERVICE_REQUEST = "SERVICE_REQUEST";
const SERVICE_ERROR = "SERVICE_ERROR";
const SERVICES_FETCH = "SERVICES_FETCH";
const SERVICE_FETCH = "SERVICE_FETCH";
const SERVICE_CREATE = "SERVICE_CREATE";
const SERVICE_EDIT = "SERVICE_EDIT";
const SERVICE_DELETE = "SERVICE_DELETE";
const SERVICE_DELETE_IMAGE = "SERVICE_DELETE_IMAGE";

// Download Action Types for services
const SERVICE_DOWNLOAD_START = "SERVICE_DOWNLOAD_START";
const SERVICE_DOWNLOAD_UPDATE = "SERVICE_DOWNLOAD_UPDATE";
const SERVICE_DOWNLOAD_FINISH = "SERVICE_DOWNLOAD_FINISH";

const BaseRoute = "companyapi/api/v1/services";

// Reusable action types object for consistency
const actionTypes = {
  fetches: {
    request: SERVICE_REQUEST,
    success: SERVICES_FETCH,
    error: SERVICE_ERROR,
  },
  fetch: {
    request: SERVICE_REQUEST,
    success: SERVICE_FETCH,
    error: SERVICE_ERROR,
  },
  create: {
    request: SERVICE_REQUEST,
    success: SERVICE_CREATE,
    error: SERVICE_ERROR,
  },
  update: {
    request: SERVICE_REQUEST,
    success: SERVICE_EDIT,
    error: SERVICE_ERROR,
  },
  delete: {
    request: SERVICE_REQUEST,
    success: SERVICE_DELETE,
    error: SERVICE_ERROR,
  },
  deleteImage: {
    request: SERVICE_REQUEST,
    success: SERVICE_DELETE_IMAGE,
    error: SERVICE_ERROR,
  },
  download: {
    request: SERVICE_REQUEST,
    start: SERVICE_DOWNLOAD_START,
    update: SERVICE_DOWNLOAD_UPDATE,
    finish: SERVICE_DOWNLOAD_FINISH,
    success: SERVICES_FETCH,
    error: SERVICE_ERROR,
  },
};

const INITIAL_STATE = {
  loading: false,
  services: [],
  service: {},
  error: "",
  downloadProgress: 0,
  isDownloading: false,
};

// Reusable handler for form data creation
const createServiceFormData = (values) => {
  const { title, description, featured, files } = values;
  const formData = new FormData();

  formData.append("title", title || " ");
  formData.append("description", description || " ");
  formData.append("featured", featured || false);

  if (files)
    for (const file of files) {
      formData.append("files", file.file);
    }
  formData.append("mediaType", "IMAGE");
  return formData;
};

// Actions using the new handlers
export const FetchServices = () => async (dispatch) => {
  await createProgressiveRequestHandler(
    dispatch,
    () =>
      cfAPI.get(BaseRoute, {
        onDownloadProgress: createProgressHandler(
          dispatch,
          actionTypes.download.update,
          actionTypes.download.finish
        ),
      }),
    actionTypes.download,
    createProgressHandler
  );
};

export const CreateService = (values) => async (dispatch) => {
  await createRequestHandler(
    dispatch,
    () => cfAPI.post(BaseRoute, createServiceFormData(values)),
    actionTypes.create,
    "Service Created"
  );
};

export const FetchService = (id) => async (dispatch) => {
  await createRequestHandler(
    dispatch,
    () => cfAPI.get(`${BaseRoute}/byId/${id}`),
    actionTypes.fetch,
    null // No toast for a fetch
  );
};

export const UpdateService = (id, values) => async (dispatch) => {
  await createRequestHandler(
    dispatch,
    () => cfAPI.patch(`${BaseRoute}/${id}`, createServiceFormData(values)),
    actionTypes.update,
    "Service Updated"
  );
};

export const DeleteService = (values) => async (dispatch) => {
  await createRequestHandler(
    dispatch,
    () => cfAPI.delete(`${BaseRoute}/${values.id}`),
    actionTypes.delete,
    "Service Deleted"
  );
};

export const DeleteServicePicture = (name, fileName) => async (dispatch) => {
  await createRequestHandler(
    dispatch,
    () => cfAPI.delete(MediaBaseRouteDelete(name, fileName)),
    actionTypes.deleteImage,
    "Image Deleted"
  );
};

// Reducer with toasts removed and state logic updated
export const ServiceReducer = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case SERVICE_DOWNLOAD_START:
      return { ...state, isDownloading: true, downloadProgress: 0 };
    case SERVICE_DOWNLOAD_UPDATE:
      return { ...state, downloadProgress: action.payload };
    case SERVICE_DOWNLOAD_FINISH:
      return { ...state, isDownloading: false, downloadProgress: 100 };
    case SERVICE_REQUEST:
      return { ...state, loading: true };
    case SERVICE_ERROR:
      return { ...state, loading: false, error: action.payload };

    case SERVICES_FETCH:
      return { ...state, loading: false, services: action.payload };
    case SERVICE_FETCH:
      return { ...state, loading: false, service: action.payload };
    case SERVICE_CREATE:
      return {
        ...state,
        loading: false,
        services: [...state.services, action.payload],
      };
    case SERVICE_EDIT:
      const updatedList = state.services.map((service) =>
        service.id === action.payload.id ? action.payload : service
      );
      return {
        ...state,
        loading: false,
        service: action.payload,
        services: updatedList,
      };
    case SERVICE_DELETE:
      const newList = state.services.filter((s) => s.id !== action.payload.id);
      return { ...state, loading: false, services: newList };
    case SERVICE_DELETE_IMAGE:
      // Assuming the payload has the new service object without the deleted image
      return { ...state, loading: false, service: action.payload };
    default:
      return state;
  }
};
